import { useState, useRef, useEffect, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon, Tick01Icon } from '@hugeicons/core-free-icons';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type SelectVariant = 'default' | 'filter' | 'compact';

export interface SelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  /** Shown in the trigger when no option matches `value` */
  placeholder?: string;
  /** Optional class for the root wrapper (label + trigger + list) */
  className?: string;
  /** When true, trigger and list span full width (default + compact variants) */
  fullWidth?: boolean;
  triggerClassName?: string;
  disabled?: boolean;
  /** `filter`: toolbar width (min160px). `compact`: header / tight controls. */
  variant?: SelectVariant;
  'aria-label'?: string;
}

const TRIGGER_SHELL =
  'flex w-full items-center justify-between gap-2 rounded-[16px] border border-solid border-surface-gray-darker bg-surface-cards font-sans text-primary transition-[border-color,box-shadow] hover:border-primary/50 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60';

const TRIGGER_VARIANT: Record<SelectVariant, string> = {
  default: 'min-h-[50px] py-3 px-4 text-base',
  filter: 'min-h-[50px] py-2.5 px-3 text-sm',
  compact: 'min-h-[42px] py-2 px-3 text-sm',
};

const LIST_SHELL =
  'absolute z-50 mt-1 left-0 end-0 w-full max-h-[280px] origin-top overflow-auto rounded-[16px] border border-solid border-surface-gray-darker bg-surface-cards py-1 font-sans text-base text-primary shadow-lg';

function nextEnabledIndex(options: SelectOption[], from: number, delta: number): number {
  const len = options.length;
  if (len === 0) return -1;
  let i = from < 0 ? (delta > 0 ? -1 : 0) : from;
  for (let s = 0; s < len; s++) {
    i = (i + delta + len) % len;
    if (!options[i]?.disabled) return i;
  }
  return -1;
}

export function Select({
  id,
  value,
  onChange,
  options,
  label,
  placeholder = '',
  className = '',
  fullWidth = false,
  triggerClassName,
  disabled = false,
  variant = 'default',
  'aria-label': ariaLabel,
}: SelectProps) {
  const { i18n } = useTranslation();
  const isRtl = i18n.language?.startsWith('ar') ?? false;
  const dir = isRtl ? 'rtl' : 'ltr';

  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    if (idx >= 0 && !options[idx]?.disabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync focused index to current value when dropdown opens
      setFocusedIndex(idx);
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: fall back to first enabled option
    setFocusedIndex(nextEnabledIndex(options, -1, 1));
  }, [open, value, options]);

  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    const el = listRef.current?.querySelector(`[data-index="${focusedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, open]);

  const rootLayoutClass =
    variant === 'filter'
      ? `w-auto min-w-[160px] max-w-full ${className}`
      : variant === 'compact'
        ? `${fullWidth ? 'w-full max-w-none' : 'w-auto min-w-[8.5rem] max-w-[14rem]'} ${className}`
        : `${fullWidth ? 'w-full max-w-none' : 'w-full max-w-xs'} ${className}`;

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((i) => nextEnabledIndex(options, i < 0 ? 0 : i, 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((i) => nextEnabledIndex(options, i < 0 ? 0 : i, -1));
      return;
    }
    if (e.key === 'Enter' && focusedIndex >= 0 && options[focusedIndex] && !options[focusedIndex].disabled) {
      e.preventDefault();
      onChange(options[focusedIndex].value);
      setOpen(false);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const triggerClasses = `${TRIGGER_SHELL} ${TRIGGER_VARIANT[variant]} ${triggerClassName ?? ''}`;
  const showPlaceholderStyle = !selectedOption && placeholder;

  return (
    <div ref={rootRef} className={`flex flex-col gap-2 font-sans ${rootLayoutClass}`} dir={dir}>
      {label && (
        <label
          id={id ? `${id}-label` : undefined}
          htmlFor={id}
          className="m-0 block w-full text-base font-normal text-gray-900 font-sans text-start"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={id}
          data-select-trigger
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={!label ? ariaLabel : undefined}
          aria-labelledby={label && id ? `${id}-label` : undefined}
          aria-activedescendant={
            open && focusedIndex >= 0 ? (id ? `${id}-option-${focusedIndex}` : undefined) : undefined
          }
          className={triggerClasses}
          dir={dir}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={handleKeyDown}
        >
          <span
            className={`min-w-0 flex-1 truncate text-start ${showPlaceholderStyle ? 'text-primary/50' : ''}`}
          >
            {displayLabel}
          </span>
          <span className={`shrink-0 text-primary/70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
            <HugeiconsIcon icon={ArrowDown01Icon} size={variant === 'compact' ? 18 : 20} />
          </span>
        </button>
        <AnimatePresence>
          {open && (
            <motion.ul
              ref={listRef}
              role="listbox"
              aria-labelledby={label && id ? `${id}-label` : undefined}
              tabIndex={-1}
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className={LIST_SHELL}
              dir={dir}
            >
              {options.map((opt, index) => {
                const isSelected = opt.value === value;
                const isFocused = index === focusedIndex;
                const isDisabled = Boolean(opt.disabled);
                return (
                  <li
                    key={`${opt.value}-${index}`}
                    data-index={index}
                    id={id ? `${id}-option-${index}` : undefined}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled}
                    className={`
                      flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-start transition-colors
                      ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}
                      ${
                        isSelected
                          ? 'bg-primary/10 font-semibold text-primary'
                          : 'text-primary font-normal'
                      }
                      ${isFocused && !isSelected && !isDisabled ? 'bg-primary/5' : ''}
                      ${!isSelected && !isDisabled ? 'hover:bg-primary/5' : ''}
                    `}
                    onMouseEnter={() => !isDisabled && setFocusedIndex(index)}
                    onClick={() => {
                      if (isDisabled) return;
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <span className="min-w-0 flex-1">{opt.label}</span>
                    {isSelected ? (
                      <HugeiconsIcon icon={Tick01Icon} size={18} className="shrink-0 text-primary" aria-hidden />
                    ) : (
                      <span className="inline-block w-[18px] shrink-0" aria-hidden />
                    )}
                  </li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
